import { useState, useEffect, useMemo, useCallback } from "react";
import { Card, Spin, Button, Message } from "@/frontend/components/common";
import { FaUsers } from "react-icons/fa";
import {
  tournamentGroupService,
  tournamentStandingService,
} from "@/frontend/services";
import type { TournamentStandingResponseDTO } from "@/shared/dto";
import type { StageConfig } from "@/core/value-objects/StageConfig";
import type { SeasonInfo, TournamentStageInfo } from "../../types";
import { StageHeader } from "../../components";
import { GroupStandingsTable } from "./GroupStandingsTable";

interface GroupsTabProps {
  stage: StageConfig;
  currentSeason: SeasonInfo;
  stages: StageConfig[];
  tournamentStages: TournamentStageInfo[];
}

export function GroupsTab({
  stage,
  currentSeason,
  stages,
  tournamentStages,
}: GroupsTabProps) {
  const [standings, setStandings] = useState<TournamentStandingResponseDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [advancingAll, setAdvancingAll] = useState(false);

  const firstStageOrder = useMemo(() => {
    if (!stages.length) return 1;
    return Math.min(...stages.map((s) => s.order));
  }, [stages]);

  const effectiveCurrentStageOrder = useMemo(() => {
    if (currentSeason?.currentStageOrder != null) {
      return currentSeason.currentStageOrder;
    }
    return firstStageOrder;
  }, [currentSeason?.currentStageOrder, firstStageOrder]);

  const expectedPlayedPerTeam = useMemo(() => {
    const teamsPerGroup = stage.structure.teamsPerGroup ?? 0;
    if (teamsPerGroup <= 1) return 0;
    const roundMultiplier = stage.structure.isDoubleLeg ? 2 : 1;
    return (teamsPerGroup - 1) * roundMultiplier;
  }, [stage.structure.teamsPerGroup, stage.structure.isDoubleLeg]);

  const canShowGroupAdvanceActions = useMemo(() => {
    return (
      Boolean(currentSeason?.isCurrent) &&
      stage.type === "GROUPS" &&
      stage.order === effectiveCurrentStageOrder
    );
  }, [currentSeason?.isCurrent, stage.type, stage.order, effectiveCurrentStageOrder]);

  const knockoutTournamentStage = useMemo(() => {
    const knockoutStage = stages.find((s) => s.type === "KNOCKOUT" && !s.isConsolation);
    if (!knockoutStage) return undefined;
    return tournamentStages.find(
      (ts) => ts.stageType === knockoutStage.type && ts.stageOrder === knockoutStage.order
    );
  }, [tournamentStages, stages]);

  const fetchStandings = useCallback(async () => {
    if (!currentSeason?.id) return;

    setLoading(true);
    try {
      const response = await tournamentStandingService.getBySeasonId(currentSeason.id);
      if (response.success && response.data) {
        setStandings(response.data);
      }
    } catch (error) {
      console.error("Error al cargar standings:", error);
    } finally {
      setLoading(false);
    }
  }, [currentSeason?.id]);

  useEffect(() => {
    fetchStandings();
  }, [fetchStandings]);

  const groupedStandings = useMemo(() => {
    if (!standings.length) return {};

    return standings.reduce(
      (acc, standing) => {
        const groupId = standing.groupId || "sin-grupo";
        if (!acc[groupId]) {
          acc[groupId] = [];
        }
        acc[groupId].push(standing);
        return acc;
      },
      {} as Record<string, TournamentStandingResponseDTO[]>
    );
  }, [standings]);

  const groupsAdvanceState = useMemo(() => {
    return Object.entries(groupedStandings)
      .sort(([groupIdA], [groupIdB]) => groupIdA.localeCompare(groupIdB))
      .map(([groupId, groupStandings]) => {
        const teamsPerGroup = stage.structure.teamsPerGroup ?? groupStandings.length;
        const hasExpectedTeams = groupStandings.length === teamsPerGroup;
        const allTeamRowsComplete = groupStandings.every(
          (standing) => standing.played >= expectedPlayedPerTeam
        );
        const canAdvanceThisGroup =
          canShowGroupAdvanceActions &&
          expectedPlayedPerTeam > 0 &&
          hasExpectedTeams &&
          allTeamRowsComplete;

        return {
          groupId,
          groupStandings,
          canAdvanceThisGroup,
        };
      });
  }, [
    groupedStandings,
    stage.structure.teamsPerGroup,
    expectedPlayedPerTeam,
    canShowGroupAdvanceActions,
  ]);

  const canAdvanceAllGroups = useMemo(() => {
    if (!groupsAdvanceState.length) return false;
    return groupsAdvanceState.every((group) => group.canAdvanceThisGroup);
  }, [groupsAdvanceState]);

  const handleAdvanceAllGroups = async () => {
    if (!currentSeason?.id || !canAdvanceAllGroups) return;

    try {
      setAdvancingAll(true);
      const response = await tournamentGroupService.advanceAllGroups({
        seasonId: currentSeason.id,
      });

      if (response.success && response.data) {
        Message.success(response.data.message || "Grupos avanzados exitosamente");
        fetchStandings();
      } else {
        Message.error("Error al avanzar todos los grupos");
      }
    } catch (error) {
      console.error("Error advancing all groups:", error);
      Message.error("Error al avanzar todos los grupos");
    } finally {
      setAdvancingAll(false);
    }
  };

  return (
    <div className="py-4">
      <StageHeader stage={stage} />
      
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Spin size="large" tip="Cargando clasificación..." />
        </div>
      ) : standings.length > 0 ? (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {groupsAdvanceState.map(({ groupId, groupStandings, canAdvanceThisGroup }) => (
              <GroupStandingsTable
                key={groupId}
                groupStandings={groupStandings}
                stage={stage}
                groupId={groupId}
                seasonId={currentSeason.id}
                knockoutStageId={knockoutTournamentStage?.id}
                showAdvanceButton={canAdvanceThisGroup}
                onAdvanceSuccess={fetchStandings}
              />
            ))}
          </div>

          {canAdvanceAllGroups && (
            <div className="fixed bottom-6 right-6 z-40">
              <Button
                type="primary"
                size="large"
                onClick={handleAdvanceAllGroups}
                loading={advancingAll}
                disabled={advancingAll}
              >
                Avanzar todos los grupos
              </Button>
            </div>
          )}
        </>
      ) : (
        <Card className="shadow-sm">
          <div className="text-center py-12">
            <FaUsers className="mx-auto text-gray-300 text-5xl mb-4" />
            <p className="text-gray-500">
              No hay datos de clasificación disponibles para esta etapa
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}
